/**
 * Have I Been Pwned (HIBP) password check using k-anonymity.
 *
 * Security rationale:
 * - The k-anonymity model sends only the **first 5 hex characters** of
 *   the SHA-1 hash of the password to the HIBP API. The server returns
 *   all hash suffixes matching that prefix. The client then checks
 *   locally whether the full hash appears in the response.
 * - This means the plaintext password (or its full hash) **never** leaves
 *   the client. The HIBP server learns nothing about the specific
 *   password being checked — it could be any of the ~400-800 hashes
 *   returned per prefix.
 * - SHA-1 is used as mandated by the HIBP API (not for security, but
 *   as a lookup key). We use WebCrypto's SHA-1 for the computation.
 *
 * @module hibp
 */

/**
 * Check whether a password has appeared in known data breaches.
 *
 * Uses the HIBP "Passwords" API (k-anonymity model) which requires
 * only a SHA-1 hash prefix. The password itself is never transmitted.
 *
 * @param password - The password to check (plaintext)
 * @returns `true` if the password was found in a breach, `false` otherwise
 * @throws If the network request fails (caller should handle gracefully —
 *         network failure should **not** block the user from proceeding)
 */
export async function isPasswordBreached(password: string): Promise<boolean> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);

  // Compute SHA-1 hash using WebCrypto
  const hashBuffer = await crypto.subtle.digest('SHA-1', data);
  const hashArray = new Uint8Array(hashBuffer);

  // Convert to uppercase hex string
  const hashHex = Array.from(hashArray)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();

  // Split into prefix (first 5 chars) and suffix (remaining 35 chars)
  const prefix = hashHex.slice(0, 5);
  const suffix = hashHex.slice(5);

  // Fetch all hash suffixes for this prefix from HIBP
  const response = await fetch(
    `https://api.pwnedpasswords.com/range/${prefix}`,
    {
      headers: {
        // Add padding to reduce information leakage about which prefix
        // was requested (HIBP supports Add-Padding header)
        'Add-Padding': 'true',
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      `HIBP API returned ${response.status}: ${response.statusText}`,
    );
  }

  const text = await response.text();

  // Each line is "SUFFIX:COUNT" — check if our suffix appears
  const lines = text.split('\n');
  for (const line of lines) {
    const [hashSuffix] = line.split(':');
    if (hashSuffix && hashSuffix.trim() === suffix) {
      return true;
    }
  }

  return false;
}
